// Copyright (c) Microsoft. All rights reserved.

import { Caption1, Card, CardHeader, makeStyles, shorthands, Text, ToggleButton } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';
import ReactMarkdown from 'react-markdown';
import { DownloadCitationDocument } from './DownloadCitationDocument';

const useClasses = makeStyles({
    root: {
        display: 'flex',

        ...shorthands.gap(customTokens.spacingVerticalS),
        flexDirection: 'column',
    },
    card: {
        display: 'flex',
        width: '100%',
        height: 'fit-content',
    },
});

interface ICitationCardsProps {
    message: IChatMessage;
}

export const CitationCards: React.FC<ICitationCardsProps & { documentId?: string; partitionNumber?: string }> = ({
    message,
    documentId,
    partitionNumber,
}) => {
    const classes = useClasses();

    const [showSnippetStates, setShowSnippetStates] = useState<boolean[]>([]);

    const initShowSnippetStates = React.useCallback(() => {
        if (!message.citations) {
            return;
        }

        // const newShowSnippetStates = message.citations.map((citation) => {
        //     return documentId && citation.link.includes(documentId) ? true : false;
        // });
        const newShowSnippetStates = message.citations.map(() => true);

        setShowSnippetStates(newShowSnippetStates);
    }, [message.citations]);

    React.useEffect(() => {
        initShowSnippetStates();
    }, [initShowSnippetStates]);

    if (!message.citations || message.citations.length === 0) {
        return null;
    }

    const showSnippet = (index: number) => {
        const newShowSnippetStates = [...showSnippetStates];
        newShowSnippetStates[index] = !newShowSnippetStates[index];
        setShowSnippetStates(newShowSnippetStates);
    };

    return (
        <div className={classes.root}>
            {message.citations.map((citation, index) => {
                // If documentId or partitionNumber is not provided, show all citations
                if (!documentId || !partitionNumber) {
                    return (
                        <Card className={classes.card} size="small" key={`citation-card-${index}`}>
                            <CardHeader
                                header={<Text weight="semibold">{citation.sourceName}</Text>}
                                description={<Caption1>Relevance score: {citation.relevanceScore.toFixed(3)}</Caption1>}
                                action={
                                    <ToggleButton
                                        appearance="transparent"
                                        icon={
                                            showSnippetStates[index] ? <ChevronUp20Regular /> : <ChevronDown20Regular />
                                        }
                                        onClick={() => {
                                            showSnippet(index);
                                        }}
                                    />
                                }
                            />
                            {showSnippetStates[index] && citation.partitions && (
                                <div>
                                    {citation.partitions.map((partition, partitionIndex) => (
                                        <div
                                            key={`partition-${index}-${partitionIndex}`}
                                            style={{
                                                border: '1px solid #ccc',
                                                borderRadius: '8px',
                                                padding: '8px',
                                                marginBottom: '8px',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                backgroundColor: '#ffffcc',
                                            }}
                                        >
                                            {citation.sourceName.toLowerCase().endsWith('.pdf') && (
                                                <DownloadCitationDocument link={citation.link} query={partition.text} />
                                            )}
                                            <ReactMarkdown
                                                components={{
                                                    h1: ({ node: _node, ...props }) => (
                                                        <h1
                                                            style={{
                                                                fontSize: '18px',
                                                                fontWeight: 'bold',
                                                                margin: '8px 0',
                                                            }}
                                                            {...props}
                                                        />
                                                    ),
                                                    h2: ({ node: _node, ...props }) => (
                                                        <h2
                                                            style={{
                                                                fontSize: '16px',
                                                                fontWeight: 'bold',
                                                                margin: '6px 0',
                                                            }}
                                                            {...props}
                                                        />
                                                    ),
                                                    h3: ({ node: _node, ...props }) => (
                                                        <h3
                                                            style={{
                                                                fontSize: '14px',
                                                                fontWeight: 'bold',
                                                                margin: '4px 0',
                                                            }}
                                                            {...props}
                                                        />
                                                    ),
                                                    code: ({
                                                        node: _node,
                                                        className: _className,
                                                        children,
                                                        ...props
                                                    }) => (
                                                        <span
                                                            style={{
                                                                backgroundColor: '#FFFFC5',
                                                                fontFamily: 'monospace',
                                                                fontWeight: 'text',
                                                                padding: '4px 8px',
                                                                fontSize: '12px',
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                                display: 'inline-block',
                                                                width: '99%',
                                                                wordWrap: 'break-word',
                                                                whiteSpace: 'pre-wrap',
                                                            }}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </span>
                                                    ),
                                                }}
                                            >
                                                {partition.text}
                                            </ReactMarkdown>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    );
                }

                // Otherwise, filter and show only matching partitions
                const matchingPartitions = citation.partitions?.filter((partition) => {
                    return (
                        partition.documentId === documentId && partition.partitionNumber.toString() === partitionNumber
                    );
                });

                if (!matchingPartitions || matchingPartitions.length === 0) {
                    return null;
                }

                return (
                    <Card className={classes.card} size="small" key={`citation-card-${index}`}>
                        <CardHeader
                            header={<Text weight="semibold">{citation.sourceName}</Text>}
                            description={<Caption1>Relevance score: {citation.relevanceScore.toFixed(3)}</Caption1>}
                            action={
                                <ToggleButton
                                    appearance="transparent"
                                    icon={showSnippetStates[index] ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                                    onClick={() => {
                                        showSnippet(index);
                                    }}
                                />
                            }
                        />
                        {showSnippetStates[index] && (
                            <div>
                                {matchingPartitions.map((partition, partitionIndex) => (
                                    <div
                                        key={`partition-${index}-${partitionIndex}`}
                                        style={{
                                            border: '1px solid #ccc',
                                            borderRadius: '8px',
                                            padding: '8px',
                                            marginBottom: '8px',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                            backgroundColor: '#ffffcc',
                                        }}
                                    >
                                        {citation.sourceName.toLowerCase().endsWith('.pdf') && (
                                            <DownloadCitationDocument link={citation.link} query={partition.text} />
                                        )}
                                        <ReactMarkdown
                                            components={{
                                                h1: ({ node: _node, ...props }) => (
                                                    <h1
                                                        style={{
                                                            fontSize: '18px',
                                                            fontWeight: 'bold',
                                                            margin: '8px 0',
                                                        }}
                                                        {...props}
                                                    />
                                                ),
                                                h2: ({ node: _node, ...props }) => (
                                                    <h2
                                                        style={{
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            margin: '6px 0',
                                                        }}
                                                        {...props}
                                                    />
                                                ),
                                                h3: ({ node: _node, ...props }) => (
                                                    <h3
                                                        style={{
                                                            fontSize: '14px',
                                                            fontWeight: 'bold',
                                                            margin: '4px 0',
                                                        }}
                                                        {...props}
                                                    />
                                                ),
                                                code: ({ node: _node, className: _className, children, ...props }) => (
                                                    <span
                                                        style={{
                                                            backgroundColor: '#FFFFC5',
                                                            fontFamily: 'monospace',
                                                            fontWeight: 'text',
                                                            padding: '4px 8px',
                                                            fontSize: '12px',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            display: 'inline-block',
                                                            width: '99%',
                                                            wordWrap: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                        {...props}
                                                    >
                                                        {children}
                                                    </span>
                                                ),
                                            }}
                                        >
                                            {partition.text}
                                        </ReactMarkdown>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};
